import type { ParsedQuery } from 'query-string'
import { useRef, useState } from 'react'

import { FvButton, FvLinkButton, LoadMore } from '@fv/client-components'
import type { ListShipment } from '@fv/client-types'

import { DividedContent } from '@/components/DividedContent'
import ExportOptions from '@/components/ExportOptions'
import InfoBox from '@/components/InfoBox'
import { QuotedByFilter } from '@/components/inputs/QuotedByFilter'
import LoadDetailPanel from '@/components/list/LoadDetailPanel'
import { ShipperTooltip } from '@/components/ShipperTooltip'
import { supportMessage } from '@/constants'
import { routes } from '@/routes'

import { HideForVendor, ShipperOnly } from '../auth/HasRole'
import { CompactFilterBar } from '../shipment-filters/CompactFilterBar'
import { ShipmentFilterPanel } from '../shipment-filters/FilterPanel'
import { useHandleFilterChange } from '../shipment-filters/hooks'
import { InProcessFiltersProvider } from '../shipment-filters/InProcessFiltersProvider'
import { vendorCreatedFilterId } from '../shipment-filters/shared-filters'
import { useUploadView } from '../shipments-upload/hooks'
import { ShipmentUploadSlider } from '../shipments-upload/ShipmentUploadSlider'
import { useInProcessShipments } from './hooks/useInProcessShipments'
import {
  useInProcessListParams,
  useIsVendorCreatedFilter,
  useSetActiveLoadId,
} from './hooks/useShipmentListParams'
import { PENDING_LOCAL_STORAGE_SORTDIR_KEY } from './hooks/useSortDirectionPreference'
import { ShipmentCard } from './ShipmentCard'
import { ShipmentSortSelect } from './ShipmentSortSelect'
import { SortDirectionToggle } from './SortDirectionToggle'

export const InProcessShipmentList = () => {
  const listRef = useRef<HTMLDivElement>(null)
  const { isOpen: uploadIsOpen } = useUploadView()
  const shipmentsQuery = useInProcessShipments({ enabled: !uploadIsOpen })
  const shipmentList: ListShipment[] = shipmentsQuery.data
  const {
    activeLoadId,
    quotedById,
    quotedByIds,
    sortBy,
    sortDirection,
    onChange,
  } = useInProcessListParams(shipmentList)
  const setActiveLoad = useSetActiveLoadId()
  const [isExporting, setIsExporting] = useState(false)
  const { handle: handleChange, searchParams } = useHandleFilterChange(listRef)
  const isVendorCreatedFilter = useIsVendorCreatedFilter()

  const isLoading =
    shipmentsQuery.isLoading ||
    (shipmentsQuery.isFetching && !shipmentList.length)

  const handleFilterChange = (data: ParsedQuery) => {
    handleChange({
      ...data,
      ...(!('quotedBy' in data) && quotedByIds.length > 0
        ? { quotedBy: quotedByIds }
        : {}),
      sortBy,
      sortDirection: sortDirection.toString(),
      ...(data.view === vendorCreatedFilterId ? { quotedBy: null } : {}),
    })
  }

  return (
    <InProcessFiltersProvider>
      <ShipmentFilterPanel
        className="b1400:col-start-1 b1400:row-start-1"
        drawerAt="b1400"
        onChange={handleFilterChange}
      />

      <div
        className="py-8 px-6 overflow-auto bg-fv-gray-100 b1400:relative b1400:col-start-1 b1400:col-span-2 b1400:row-start-1"
        id="shipmentList"
        ref={listRef}
      >
        <nav className="mb-6 gap-x-2 items-center flex b1600:flex-wrap">
          {!isVendorCreatedFilter && (
            <HideForVendor>
              <QuotedByFilter
                className="flex-1 max-w-52 b1600:max-w-none b1600:w-full b1600:flex-none b1600:mb-4 b1400:mb-0 b1400:max-w-52 b1400:w-auto truncate"
                disabled={quotedByIds.length > 1}
                quotedById={quotedByIds.length > 1 ? null : quotedById}
                onChange={quotedById =>
                  onChange({ quotedBy: !quotedById ? null : [quotedById] })
                }
              />
            </HideForVendor>
          )}

          <div className="flex items-center gap-1">
            <SortDirectionToggle
              sortDirection={sortDirection}
              localStorageKey={PENDING_LOCAL_STORAGE_SORTDIR_KEY}
            />{' '}
            <span className="b1100:hidden">by</span>
            <ShipmentSortSelect className="mx-1" value={sortBy} />
          </div>
          <DividedContent>
            <ShipperOnly permissions={['load-exporter']}>
              <FvButton icon="file-export" onClick={() => setIsExporting(true)}>
                Export
              </FvButton>
            </ShipperOnly>

            <ShipperOnly permissions={['load-creator']}>
              <div>
                <ShipperTooltip label="Manage uploads">
                  <FvLinkButton icon="archive" to={routes.uploads()}>
                    Uploads
                  </FvLinkButton>
                </ShipperTooltip>

                {/* <div className="hidden b1700:inline">
                  <ShipperTooltip label="Manage uploads">
                    <FvLinkButton icon="archive" to={routes.uploads()} />
                  </ShipperTooltip>
                </div> */}
              </div>
            </ShipperOnly>
          </DividedContent>

          <div className="b1400:block border-t-fv-gray mt-4 hidden basis-full border-t border-dashed pt-4">
            <CompactFilterBar onChange={handleFilterChange} />
          </div>
        </nav>

        {!uploadIsOpen && (
          <>
            {shipmentsQuery.isError && (
              <InfoBox icon="triangle">
                Unable to load shipments, {supportMessage}
              </InfoBox>
            )}

            {isLoading ? (
              <InfoBox icon="spinner">Loading...</InfoBox>
            ) : (
              <>
                {shipmentList.map(load => (
                  <ShipmentCard
                    isActive={load.loadId === activeLoadId}
                    key={load.loadId}
                    onClick={() => setActiveLoad(load.loadId)}
                    shipment={load}
                  />
                ))}

                {!shipmentList.length && (
                  <InfoBox>No shipments available.</InfoBox>
                )}
              </>
            )}

            <LoadMore
              className="mt-4"
              fetchNextPage={shipmentsQuery.fetchNextPage}
              hasNextPage={shipmentsQuery.hasNextPage}
              isLoading={shipmentsQuery.isFetchingNextPage}
            />
          </>
        )}
      </div>

      {!uploadIsOpen && (
        <LoadDetailPanel key={activeLoadId} loadId={activeLoadId} />
      )}
      <ShipmentUploadSlider />
      <ExportOptions
        closePanel={() => setIsExporting(false)}
        isOpen={isExporting}
        queryString={searchParams.toString()}
        listType={'in-process'}
      />
    </InProcessFiltersProvider>
  )
}
